import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { IconDownload, IconRefresh } from '@tabler/icons-react';
import styles from './ReportMetrics.module.scss';
import { ProjectDto, PermissionKey } from '../../api/model';
import { usePostIntelligenceKpisProjectProjectIdRecompute } from '../../api/kpi-api/kpi-api';
import { useProjectIntelligence } from '../../hooks/useProjectIntelligence';
import { useCurrentUserPermissions } from '../../hooks/useCurrentUserPermissions';
import { indexProjectKpis, lastComputedAt } from './intelligence';
import LastComputed from './widgets/LastComputed';
import { ObjectTarget, indexHistory, objectSearch } from './detail';
import Overview from './views/Overview';
import Execution from './views/Execution';
import Readiness from './views/Readiness';
import Quality from './views/Quality';
import Dependencies from './views/Dependencies';
import Trends from './views/Trends';
import Actions from './views/Actions';
import type { ProjectSection, ReportViewProps } from './views/viewProps';

interface ReportMetricsProps {
  project?: ProjectDto;
}

/**
 * The seven Detailmasken of the customer's 2026-09-22 spec, in its navigation
 * order. The Portfolio Overview is no longer one of them: it is not about a
 * project, and lives at the workspace level (`/metrics`).
 */
export const REPORT_TABS = [
  'overview',
  'execution',
  'readiness',
  'quality',
  'dependencies',
  'trends',
  'actions',
] as const;
export type ReportTab = (typeof REPORT_TABS)[number];

/**
 * Where links to the old four-page report land: the Executive Summary became
 * the Übersicht, and Quality & Dependencies split in two.
 */
export const LEGACY_REPORT_TABS: Record<string, ReportTab> = {
  summary: 'overview',
  portfolio: 'overview',
};

const VIEWS: Record<ReportTab, React.FC<ReportViewProps>> = {
  overview: Overview,
  execution: Execution,
  readiness: Readiness,
  quality: Quality,
  dependencies: Dependencies,
  trends: Trends,
  actions: Actions,
};

/** Long enough for the charts to lay out and finish animating before the dialog opens. */
const PRINT_RENDER_DELAY_MS = 1200;

/**
 * Shell for the Project Intelligence report: fetches the engine's output once
 * and hands it to the open tab, so switching tabs costs nothing and no two
 * tabs can show different numbers for the same KPI.
 */
const ReportMetrics: React.FC<ReportMetricsProps> = ({ project }) => {
  const { t } = useTranslation(['templates', 'gantt']);
  const navigate = useNavigate();
  const { report } = useSearch({ from: '/project/$projectId' });
  const activeTab: ReportTab = report ?? 'overview';

  const {
    kpis,
    alerts,
    history,
    phases,
    milestones,
    dependencyGraph,
    findings,
    actionPoints,
    isLoading,
    isError,
    refetch,
  } = useProjectIntelligence(project?.id);

  // KPIs are recomputed on a queue now, so what is on screen can be a few
  // seconds behind the last edit. The page says how old it is, and offers the
  // one endpoint that still computes in line for when that is not good enough.
  const { can } = useCurrentUserPermissions();
  const canRecompute = can(PermissionKey.TASKS, 'UPDATE');
  const recompute = usePostIntelligenceKpisProjectProjectIdRecompute();

  const refreshNow = useCallback(() => {
    if (!project?.id) return;
    recompute.mutate({ projectId: project.id }, { onSuccess: () => refetch() });
  }, [project?.id, recompute, refetch]);

  // The project payload does not always carry its phases, so the separately
  // loaded list wins; without a name a scope id would surface raw as "#12".
  // Named the same way the rest of the app does: a custom name if there is one,
  // otherwise the translated phase type rather than the stored key.
  const phaseNames = useMemo(() => {
    const names = new Map<number, string>();
    // Schedule order, so every per-phase table and chart reads start to finish
    // rather than in whatever order the phases were created.
    const ordered = [...(phases ?? project?.phases ?? [])].sort(
      (a, b) =>
        (a?.startTime ?? '9999').localeCompare(b?.startTime ?? '9999') || (a?.id ?? 0) - (b?.id ?? 0),
    );
    for (const phase of ordered) {
      if (phase?.id == null) continue;
      const fallback = phase.name || `#${phase.id}`;
      names.set(
        phase.id,
        phase.customName || (phase.type ? t(`gantt:phaseTypes.${phase.type}`, fallback) : fallback),
      );
    }
    return names;
  }, [phases, project?.phases, t]);

  const index = useMemo(() => indexProjectKpis(kpis), [kpis]);
  const historyIndex = useMemo(() => indexHistory(history), [history]);
  const computedAt = useMemo(() => lastComputedAt(kpis), [kpis]);

  const subtitle = project?.name
    ? t('report.subtitle', { project: project.name })
    : t('report.subtitleFallback');

  // Export renders every report page at once and hands them to the browser's
  // print/PDF dialog - see the print rules in ReportMetrics.module.scss.
  const [isPrinting, setIsPrinting] = useState(false);

  useEffect(() => {
    if (!isPrinting) return undefined;
    const timer = window.setTimeout(() => window.print(), PRINT_RENDER_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [isPrinting]);

  useEffect(() => {
    const done = () => setIsPrinting(false);
    window.addEventListener('afterprint', done);
    return () => window.removeEventListener('afterprint', done);
  }, []);

  const exportReport = useCallback(() => setIsPrinting(true), []);

  // The page frame is one screen tall and its content area scrolls, not the
  // window - so a new tab scrolls that area back to its very top, project
  // header included, the same on every switch.
  const topRef = useRef<HTMLDivElement>(null);

  const selectTab = useCallback(
    (tab: ReportTab) => {
      void navigate({
        to: '/project/$projectId',
        params: { projectId: String(project?.id ?? '') },
        search: (previous) => ({ ...previous, report: tab }),
        replace: true,
      });
      let scroller = topRef.current?.parentElement ?? null;
      while (scroller && !/(auto|scroll)/.test(getComputedStyle(scroller).overflowY)) {
        scroller = scroller.parentElement;
      }
      scroller?.scrollTo({ top: 0 });
    },
    [navigate, project?.id],
  );

  const openSection = useCallback(
    (section: ProjectSection) => {
      void navigate({
        to: '/project/$projectId',
        params: { projectId: String(project?.id ?? '') },
        search: (previous) => ({ ...previous, section, report: undefined }),
      });
    },
    [navigate, project?.id],
  );

  const header = (
    <>
      <div ref={topRef} className={styles.planHeader}>
        <div className={styles.planHeaderContent}>
          <h2 className={styles.planTitle}>{t('report.title')}</h2>
          <p className={styles.planDescription}>{subtitle}</p>
        </div>
        <div className={styles.planHeaderActions}>
          <LastComputed at={computedAt} />
          {canRecompute && (
            <button
              type="button"
              className={styles.exportButton}
              onClick={refreshNow}
              disabled={recompute.isPending}
            >
              <IconRefresh size={16} stroke={1.8} />
              {recompute.isPending ? t('report.refreshing') : t('report.refresh')}
            </button>
          )}
          <button
            type="button"
            className={styles.exportButton}
            onClick={exportReport}
            disabled={isPrinting}
          >
            <IconDownload size={16} stroke={1.8} />
            {isPrinting ? t('report.exportPreparing') : t('report.export')}
          </button>
        </div>
      </div>

      <div className={styles.tabBar} role="tablist">
        {REPORT_TABS.map((tab) => (
          <button
            key={tab}
            type="button"
            role="tab"
            aria-selected={tab === activeTab}
            className={`${styles.tab} ${tab === activeTab ? styles.tabActive : ''}`}
            onClick={() => selectTab(tab)}
          >
            {t(`report.tabs.${tab}`)}
          </button>
        ))}
      </div>
    </>
  );

  const message = (text: string) => (
    <>
      {header}
      <div className={styles.dashboard}>
        <p className={styles.planDescription}>{text}</p>
      </div>
    </>
  );

  if (isLoading) return message(t('report.state.loading'));
  if (isError) return message(t('report.state.error'));

  // No values at all means the engine has never run for this project - every
  // write path triggers a recompute, so this clears itself on the next change.
  if ((kpis?.length ?? 0) === 0) return message(t('report.state.empty'));

  const View = VIEWS[activeTab];
  const openObject = (target: ObjectTarget) => {
    void navigate({
      to: '/project/$projectId',
      params: { projectId: String(project?.id ?? '') },
      search: (previous) => ({ ...previous, ...objectSearch(target) }),
      replace: true,
    });
  };

  const viewProps: ReportViewProps = {
    project,
    kpis,
    alerts,
    history,
    historyIndex,
    milestones,
    dependencyGraph,
    findings,
    actionPoints,
    index,
    phaseNames,
    openTab: selectTab,
    openSection,
    openObject,
  };

  return (
    <>
      {header}
      <View {...viewProps} />

      {isPrinting && (
        <div className={styles.printRoot} data-print-root>
          {REPORT_TABS.map((tab) => {
            const PrintView = VIEWS[tab];
            return (
              <section key={tab} className={styles.printPage}>
                <h2 className={styles.printTitle}>{t(`report.tabs.${tab}`)}</h2>
                <p className={styles.printSubtitle}>{subtitle}</p>
                <PrintView {...viewProps} />
              </section>
            );
          })}
        </div>
      )}
    </>
  );
};

export default ReportMetrics;
